import Phaser from 'phaser';

import { BlackjackMainScene } from '../../scenes/blackjack-main-scene';
import { StartViewModel } from './start-view-model';
import { Subscription } from 'rxjs';
import { TextResizer } from 'src/games/common/phaser/game/ui/text-resizer';
import { ResizeMetrics } from '../../model/resize-metrics';

export class StartView extends Phaser.GameObjects.Container {
  private subscriptionGroup: Subscription;
  private textResizer: TextResizer;

  private startButton: Phaser.GameObjects.Image;
  private buttonIcon: Phaser.GameObjects.Image;
  private buttonLabel: Phaser.GameObjects.Text;
  private buttonAnimation: Phaser.Tweens.Tween;

  constructor(
    scene: BlackjackMainScene,
    x: number,
    y: number,
    private viewModel: StartViewModel,
  ) {
    super(scene, x, y);

    this.subscriptionGroup = new Subscription();
    this.textResizer = new TextResizer(scene, this, {
      fontFamily: viewModel.defaultFontFamily,
      fontSize: '50px',
      fontStyle: 'bold',
      color: viewModel.primaryLabelColor,
    });

    this.startButton = scene.add
      .image(0, 0, viewModel.buttonAsset.type)
      .setOrigin(0.5, 0.5)
      .setInteractive();
    this.add([this.startButton]);

    this.buttonIcon = scene.add.image(0, 0, viewModel.buttonIcon.type).setOrigin(0.5, 0.5);
    this.add([this.buttonIcon]);

    this.buttonLabel = this.scene.add
      .text(0, 0, this.viewModel.playLabelText, {
        fontFamily: viewModel.defaultFontFamily,
        fontSize: '50px',
        fontStyle: 'bold',
        color: viewModel.primaryLabelColor,
      })
      .setOrigin(0.5, 0.5);
    this.add([this.buttonLabel]);

    this.startButton.on(Phaser.Input.Events.POINTER_UP, (_pointer: Phaser.Input.Pointer) => {
      viewModel.startClicked();
      this.showButton(false);
    });

    this.setName(StartView.name);
    scene.add.existing(this);

    this.buttonAnimation = scene.tweens.add({
      targets: this,
      scaleX: 1.1,
      scaleY: 1.1,
      yoyo: true,
      repeat: -1, // Infinite
      duration: 1200,
      ease: 'Sine.easeInOut',
      paused: false,
    });

    this.subscriptionGroup.add(
      viewModel.onShowStartButtonTrigger?.subscribe((show) => {
        this.showButton(show);
      }),
    );
  }

  override destroy(_fromScene?: boolean | undefined): void {
    this.subscriptionGroup.unsubscribe();

    super.destroy(_fromScene);
  }

  public onResize(resizeMetrics: ResizeMetrics) {
    const layoutConfig = resizeMetrics.layoutConfig;
    const scalingMetrics = resizeMetrics.scalingMetrics;

    const cellSz = layoutConfig.boardHeight / 9;
    const shapeBoardHeight = cellSz * 3;

    const xPos = layoutConfig.boardWidth * 0.5;
    const yPos = layoutConfig.boardHeight * 0.9;

    this.setPosition(
      xPos * scalingMetrics.scaleFactor + scalingMetrics.scaledOffsetX,
      yPos * scalingMetrics.scaleFactor,
    );
    this.setSize(
      layoutConfig.boardWidth * scalingMetrics.scaleFactor,
      shapeBoardHeight * scalingMetrics.scaleFactor,
    );

    const buttonScale = 1.0;
    this.startButton.setDisplaySize(
      this.viewModel.buttonAsset.dimensions.width * scalingMetrics.scaleFactor * buttonScale,
      this.viewModel.buttonAsset.dimensions.height * scalingMetrics.scaleFactor * buttonScale,
    );

    const iconScale = 0.5;
    this.buttonIcon.setDisplaySize(
      this.viewModel.buttonIcon.dimensions.width * scalingMetrics.scaleFactor * iconScale,
      this.viewModel.buttonIcon.dimensions.height * scalingMetrics.scaleFactor * iconScale,
    );

    const availableWidth = (this.startButton.displayWidth - this.buttonIcon.displayWidth) * 0.6;

    const fontSize = this.textResizer.fitText('XXXXXXXXX', availableWidth, 14);
    this.buttonLabel.setFontSize(fontSize);

    const spacing = this.buttonIcon.displayWidth * 0.2;
    this.buttonIcon.setX(-spacing * 0.5 - this.buttonLabel.displayWidth * 0.5);
    this.buttonLabel.setX(spacing * 0.5 + this.buttonIcon.displayWidth * 0.5);
  }

  private showButton(show: boolean) {
    this.visible = show;
    if (show) {
      this.startButton.setInteractive();
      this.buttonAnimation.play();
    } else {
      this.startButton.disableInteractive();
      this.buttonAnimation.stop();
      this.setScale(1.0);
    }
  }
}
